<template>
<Layout>
  <SEOStructuredData 
    :data="structuredData"
    type="website"
  />
  <div class="licoin">
    <div class="container">
      <h1>LiCoin 錢包</h1>

      <!-- Loading state -->
      <div v-if="initialLoading" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Content when loading is complete -->
      <template v-else>
        <!-- 沒有錢包時顯示使用條款 -->
        <div v-if="!hasWallet" class="terms-section">
          <h2>使用條款</h2>
          <div class="terms-content">
            <p>1. LiCoin 是我們的虛擬貨幣，可以用於購買商品和服務。</p>
            <p>2. 每個帳戶只能創建一個 LiCoin 錢包。</p>
            <p>3. LiCoin 不可兌換成現金。</p>
            <p>4. 我們保留隨時修改使用條款的權利。</p>
          </div>
          <div class="terms-agreement">
            <label>
              <input type="checkbox" v-model="agreedToTerms">
              我已閱讀並同意以上條款
            </label>
          </div>
          <button 
            class="btn btn-primary" 
            @click="createWallet" 
            :disabled="!agreedToTerms || loading"
          >
            {{ loading ? '處理中...' : '創建錢包' }}
          </button>
        </div>

        <!-- 有錢包時顯示錢包信息 -->
        <div v-else-if="wallet" class="wallet-section">
          <div class="wallet-info">
            <h2>錢包信息</h2>
            <div class="info-item">
              <label>錢包地址：</label>
              <span>{{ wallet.address }}</span>
            </div>
            <div class="info-item">
              <label>餘額：</label>
              <div class="licoin-balance">
                <span>{{ wallet.licBalance }} LiCoin <a class="btn btn-sm btn-primary" @click="showPurchaseModal">購買 LiCoin</a></span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="wallet-section">
          <div class="wallet-info">
            <h2>錢包信息</h2>
            <div class="info-item">
              <label>錢包地址：</label>
              <span>尚未取得錢包資料</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Modal -->
  <div class="modal fade" id="purchaseModal" tabindex="-1" aria-labelledby="purchaseModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="purchaseModalLabel">購買 LiCoin</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="amount" class="form-label">數量</label>
            <input type="number" class="form-control" id="amount" v-model="purchaseAmount" min="1">
          </div>
          <div class="mb-3">
            <p>總金額: {{ purchaseAmount * 100 }} 元</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">取消</button>
          <button type="button" class="btn btn-primary" @click="confirmPurchase">確認購買</button>
        </div>
      </div>
    </div>
  </div>
</Layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue'
import { mapActions, mapState } from 'vuex'
import SEOStructuredData from '@/components/SEOStructuredData.vue'

export default {
  name: 'LiCoin',
  data() {
    return {
      wallet: null,
      hasWallet: false,
      agreedToTerms: false,
      loading: false,
      initialLoading: true,
      purchaseAmount: 1
    }
  },
  computed: {
    ...mapState(['storeInfo']),
    structuredData() {
      const storeName = this.storeInfo?.name || '購物平台'
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': `LiCoin 錢包 - ${storeName}`,
        'description': `${storeName}LiCoin 虛擬貨幣錢包，安全便捷的數位支付服務`,
        'url': window.location.href
      }
    }
  },
  components: {
    Layout,
    SEOStructuredData
  },
  methods: {
    ...mapActions(['fetchWallet', 'createWallet', 'purchaseLiCoin', 'fetchStoreInfo']),
    async createWallet() {
      if (!this.agreedToTerms) return

      this.loading = true
      try {
        await this.createLiCoinWallet()
        this.hasWallet = true
        await this.fetchWalletInfo()
      } catch (error) {
        console.error('創建錢包失敗：', error)
      } finally {
        this.loading = false
      }
    },
    async fetchWalletInfo() {
      try {
        const response = await this.fetchLiCoinWallet()
        if (response.wallet && !response.error) {
          this.wallet = response.wallet
          this.hasWallet = true
        } else {
          // API 回傳 error: "Wallet not found"
          this.wallet = null
          this.hasWallet = false
        }
      } catch (error) {
        this.wallet = null
        if (error.response?.status === 404) {
          this.hasWallet = false
        } else {
          console.error('獲取錢包信息失敗：', error)
        }
      } finally {
        this.initialLoading = false
      }
    },
    showPurchaseModal() {
      this.$nextTick(() => {
        const modalElement = document.getElementById('purchaseModal')
        if (modalElement) {
          if (!this.purchaseModal) {
            this.purchaseModal = new bootstrap.Modal(modalElement)
          }
          this.purchaseModal.show()
        }
      })
    },
    confirmPurchase() {
      if (this.purchaseModal) {
        this.purchaseModal.hide()
      }
      this.$router.push({
        name: 'BuyLiCoin',
        query: { amount: this.purchaseAmount }
      })
    }
  },
  mounted() {
    this.fetchStoreInfo()
  },
  metaInfo() {
    const storeName = this.storeInfo?.name || '購物平台'
    
    return {
      title: `LiCoin 錢包 - ${storeName}`,
      meta: [
        { name: 'description', content: `${storeName}LiCoin 虛擬貨幣錢包，安全便捷的數位支付服務` },
        { name: 'keywords', content: `${storeName},LiCoin,虛擬貨幣,數位支付,錢包` },
        { property: 'og:title', content: `LiCoin 錢包 - ${storeName}` },
        { property: 'og:description', content: `${storeName}LiCoin 虛擬貨幣錢包，安全便捷的數位支付服務` },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: window.location.href },
        { name: 'twitter:title', content: `LiCoin 錢包 - ${storeName}` },
        { name: 'twitter:description', content: `${storeName}LiCoin 虛擬貨幣錢包，安全便捷的數位支付服務` }
      ]
    }
  },
  async created() {
    await this.fetchWalletInfo()
  },
  beforeDestroy() {
    if (this.purchaseModal) {
      this.purchaseModal.dispose()
    }
  }
}
</script>

<style scoped>
.licoin {
  margin-top: 80px;
  padding: 2rem 0;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
}

h1 {
  text-align: center;
  margin-bottom: 2rem;
  color: #333;
}

.terms-section {
  background: #fff;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.terms-content {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.terms-content p {
  margin-bottom: 0.5rem;
}

.terms-agreement {
  margin: 1.5rem 0;
}

.wallet-section {
  background: #fff;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.wallet-info {
  margin-top: 1rem;
}

.info-item {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.info-item label {
  font-weight: bold;
  width: 100px;
  color: #666;
}

.info-item span {
  color: #333;
}

/* Spinner styles */
.spinner-border {
  width: 3rem;
  height: 3rem;
  margin: 2rem auto;
}

.text-center {
  text-align: center;
}
</style> 